import { useMemo, useState } from 'react'
import type { ConnectionStatus } from '../../hooks/use-sse-stream.ts'
import { useBeaconRunStream, useGetBeaconRun } from '../../utils/api-client/api-hooks.ts'
import type { BeaconStreamFrame } from '../../utils/api-client/sse-schemas.ts'
import { type BeaconRunState, EMPTY_BEACON_RUN, foldFrames, withPlatformOutcome } from './beacon-run.ts'

type Folded = {
  runId: string | undefined
  frames: readonly BeaconStreamFrame[]
  run: BeaconRunState
  settledSeq: number
}

export type UseBeaconRun = {
  run: BeaconRunState
  /**
   * The last seq of the first batch this viewer received. Everything up to it is history a reload
   * replayed, drawn as it stands; only what arrives after it animates in.
   */
  settledSeq: number
  connectionStatus: ConnectionStatus
  /** False once the events endpoint answered 404: the run's stream is gone and nothing will replay. */
  isAvailable: boolean
  error?: Error
}

const initialFold = (runId: string | undefined): Folded => ({
  runId,
  frames: [],
  run: EMPTY_BEACON_RUN,
  settledSeq: 0,
})

/** Whether `next` only appended to `previous`, so folding can resume where it left off. */
const isAppendOf = (next: readonly BeaconStreamFrame[], previous: readonly BeaconStreamFrame[]) =>
  next.length >= previous.length && (previous.length === 0 || next[previous.length - 1] === previous.at(-1))

/**
 * Follows a Beacon run's stream and folds it into run state as frames arrive. Once the platform has
 * finished the run, its state is the run's status, whatever the stream last said.
 *
 * Folding resumes from the last frame seen instead of replaying the whole stream on each one. A
 * reconnect can replay frames already seen; the fold ignores frames at or before the state's seq, so
 * the replay neither double-counts nor blanks the picture while it catches up. Pass `undefined` to stay
 * idle.
 */
export const useBeaconRun = (runId: string | undefined): UseBeaconRun => {
  const stream = useBeaconRunStream(runId)
  const { data: platformRun } = useGetBeaconRun({ runId })
  const [folded, setFolded] = useState<Folded>(() => initialFold(runId))

  // Derived during render (React's "adjust state when a prop changes" pattern) so a frame shows up in the
  // same commit it arrives in, with no effect lagging a render behind.
  // On a new run the stream still holds the old run's frames until its effect resets; counting them as
  // already seen keeps them out of the new run's fold.
  const current = folded.runId === runId ? folded : { ...initialFold(runId), frames: stream.events }
  if (current !== folded || stream.events !== current.frames) {
    const fresh = isAppendOf(stream.events, current.frames) ? stream.events.slice(current.frames.length) : stream.events
    const run = foldFrames(current.run, fresh)
    const settledSeq = current.settledSeq === 0 && run.seq > 0 ? run.seq : current.settledSeq
    setFolded({ runId, frames: stream.events, run, settledSeq })
  }

  const foldedRun = folded.runId === runId ? folded.run : EMPTY_BEACON_RUN
  const run = useMemo(() => withPlatformOutcome(foldedRun, platformRun), [foldedRun, platformRun])

  return {
    run,
    settledSeq: folded.runId === runId ? folded.settledSeq : 0,
    connectionStatus: stream.connectionStatus,
    isAvailable: stream.isEndpointAvailable,
    error: stream.error,
  }
}
